import React, { useState, useEffect, useContext } from "react";
import CommunicationGrid from "../Components/CommunicationGrid";
import { ToastContext } from "../utils/ToastContext";
import { useSelector } from "react-redux";
import { handleDateFormat } from "../utils/Common";
import Loader from "../Components/Loader";
import MUISearchBar from "../Components/MUISearchBar";
import { Box } from "@mui/material";
import DashboardDropdown from "../Components/DashboardDropdown";
import GridPairDatePicker from "../Components/GridPairDatePickerv2";
import TimelineSelector from "../Components/TimelineSelector";
import dayjs from "dayjs";
import isoWeek from "dayjs/plugin/isoWeek";
import LeadDataModal from "../Components/LeadDataModal";
import CommunicationTemplateFormDrawer from "../Components/CommunicationTemplateFormDrawer";
import ActionMenu from "../Components/ActionMenu";
import { getSMSTemplatesList } from "../Services/templatesService/SMSTemplateService";
dayjs.extend(isoWeek);

function CommunicationTemplate() {
  const userDetails = useSelector(
    (state) => state.user.userDetails || { UserDetails: {} },
  );

  const [drawerMode, setDrawerMode] = useState("create");
  const [selectedTemplate, setSelectedTemplate] = useState(null);

  const [modalVisibility, setModalVisibility] = useState({
    datePicker: false,
    addTemplateDrawer: false,
  });

  const [tableState, setTableState] = useState({
    tableColumns: [],
    tableData: [],
    totalRecords: 0,
    pageNumber: 1,
    recordsPerPage: 25,
  });

  const thisWeekStart = dayjs().startOf("isoWeek").format("YYYY-MM-DD");
  const today = dayjs().format("YYYY-MM-DD");

  const [filterState, setFilterState] = useState({
    dateRange: { fromDate: "", toDate: "" },
    customDateRange: { fromDate: "", toDate: "" },
    searchQuery: "",
  });

  const [rawData, setRawData] = useState([]);
  const [manualTrigger, setManualTrigger] = useState(0);
  const [remountKey, setRemountKey] = useState(0);
  const [filterChangeCounter, setFilterChangeCounter] = useState(0);
  const [resetCustomDate, setResetCustomDate] = useState(true);
  const [timeLineSelected, setTimeLineSelected] = useState(false);
  const [leadDataModalOpen, setLeadDataModalOpen] = useState(false);
  const [selectedLeadData, setSelectedLeadData] = useState(null);
  const [timelineValue, setTimelineValue] = useState("All Time");

  useEffect(() => {
    document.title = "Communication Template";
  }, []);

  const [loader, setLoader] = useState(false);
  const { showToast } = useContext(ToastContext);

  const initialColumns = useSelector(
    (state) => state.leadsColumn.selectedColumns,
  );

  const initialSelectedColumns = new Set(initialColumns);
  const [selectedColumns, setSelectedColumns] = useState(
    initialSelectedColumns,
  );

  const updateModalVisibility = (modalName, isVisible) => {
    setModalVisibility((prev) => ({ ...prev, [modalName]: isVisible }));
  };

  const updateTableState = (key, value) => {
    setTableState((prev) => ({ ...prev, [key]: value }));
  };

  const updateFilterState = (key, value) => {
    setFilterChangeCounter((prev) => prev + 1);
    setFilterState((prev) => ({ ...prev, [key]: value }));
    handlePageReset();
  };

  // Fetch communication template data
  const getCommunicationTemplateData = async () => {
    setLoader(true);
    try {
      const data = await getSMSTemplatesList(
        {
          currentPage: parseInt(tableState.pageNumber),
          recordsPerPage: tableState.recordsPerPage,
          searchQuery: filterState.searchQuery,
        },
        filterState.dateRange.fromDate,
        filterState.dateRange.toDate,
      );

      if (data && data.data.count > 0 && Array.isArray(data?.data?.templates)) {
        const templateColumns = [
          { key: "id", header: "ID" },
          { key: "templateName", header: "Template Name" },
          { key: "templateContent", header: "Content" },
          { key: "isActive", header: "Active" },
          { key: "providerId", header: "Provider ID" },
          { key: "templateId", header: "Template ID" },
          { key: "trackLinkId", header: "Track Link ID" },
          { key: "createdon", header: "Created On" },
        ];

        updateTableState("totalRecords", data.data.count);
        updateTableState("tableColumns", templateColumns);
        setSelectedColumns(new Set(templateColumns.map((col) => col.header)));

        setRawData(data?.data?.templates);
        updateTableState("tableData", generateTableData(data?.data?.templates));
      } else {
        updateTableState("totalRecords", 0);
        updateTableState("tableColumns", data?.data?.templates);
        updateTableState("tableData", generateTableData([]));
      }
    } catch (error) {
      console.error(error);
      showToast("Something Went Wrong", "error");
    }
    setLoader(false);
  };

  useEffect(() => {
    getCommunicationTemplateData();
  }, [
    filterState.dateRange,
    tableState.pageNumber,
    tableState.recordsPerPage,
    manualTrigger,
    filterState.searchQuery,
  ]);

  const handlePageRefresh = () => {
    setManualTrigger((prev) => prev + 1);
  };

  const generateTableData = (templatesData) => {
    return (
      templatesData?.map((item) => ({
        id: item.id,
        templateName: item.templateName,
        templateContent: item.templateContent,
        isActive: item.isActive ? "Yes" : "No",
        providerId: item.providerId || "-",
        templateId: item.templateId || "-",
        trackLinkId: item.trackLinkId || "-",
        createdon: item.createdon ? handleDateFormat(item.createdon) : "-",
        leadsjson: item,
      })) || []
    );
  };

  // Filter table columns based on user selection
  const filteredTableColumns = tableState.tableColumns.filter((column) =>
    selectedColumns.has(column.header),
  );

  // Filter table data based on selected columns
  const filteredTableData = tableState.tableData.map((row) => {
    const filteredRow = {
      leadsId: row.leadsId,
      leadsjson: row.leadsjson || row.leadsJson || row,
    };
    Object.keys(row).forEach((key) => {
      const columnName = tableState.tableColumns.find(
        (col) => col.key === key,
      )?.header;
      if (selectedColumns.has(columnName)) {
        filteredRow[key] = row[key];
      }
    });
    return filteredRow;
  });

  const handleLeadNameClick = (leadData) => {
    setSelectedLeadData(leadData);
    setLeadDataModalOpen(true);
  };

  const handlePageChange = (currentPage, recordsPerPage) => {
    updateTableState("pageNumber", parseInt(currentPage));
    updateTableState("recordsPerPage", recordsPerPage);
  };

  const handlePageReset = () => {
    updateTableState("pageNumber", parseInt(1));
  };

  const handleClearFilter = () => {
    updateModalVisibility("datePicker", false);
    setResetCustomDate((prev) => !prev);
    setFilterChangeCounter((prev) => prev + 1);
    handlePageReset();
    setRemountKey((prevKey) => prevKey + 1);
    setTimelineValue("All Time");
    setTimeLineSelected(false);
    setFilterState({
      dateRange: { fromDate: "", toDate: "" },
      searchQuery: "",
    });
    localStorage.removeItem("searchCriteria");
    localStorage.removeItem("savedAdvanceSearch");
    localStorage.removeItem("queryConnectorLeads");
    window.dispatchEvent(new CustomEvent("searchCriteriaUpdated"));
  };

  const handleCustomDateChange = (dateRange) => {
    updateFilterState("dateRange", {
      fromDate: dateRange.fromDate,
      toDate: dateRange.toDate,
    });
  };

  const handleDateClearButton = () => {
    updateFilterState("dateRange", { fromDate: "", toDate: "" });

    updateModalVisibility("datePicker", false);

    setTimelineValue("All Time");

    handleTimelineChange("All Time");
  };

  const handleTimelineChange = (value) => {
    setTimelineValue(value);
    updateModalVisibility("datePicker", false);

    const updateDateRangeAndFetchData = (fromDate, toDate) => {
      updateFilterState("dateRange", { fromDate, toDate });
    };

    if (value === "Custom Date") {
      setTimeLineSelected(true);
      updateModalVisibility("datePicker", true);
      return;
    } else {
      setTimeLineSelected(false);
    }

    switch (value) {
      case "All Time":
        updateDateRangeAndFetchData("", "");
        break;
      case "Custom Date":
        updateModalVisibility("datePicker", true);
        break;
      case "Yesterday": {
        const yesterday = dayjs().subtract(1, "day").format("YYYY-MM-DD");
        updateDateRangeAndFetchData(yesterday, yesterday);
        break;
      }
      case "Today": {
        const today = dayjs().format("YYYY-MM-DD");
        updateDateRangeAndFetchData(today, today);
        break;
      }
      case "Last Week": {
        const lastWeekStart = dayjs()
          .subtract(1, "week")
          .startOf("isoWeek")
          .format("YYYY-MM-DD");
        const lastWeekEnd = dayjs()
          .subtract(1, "week")
          .endOf("isoWeek")
          .format("YYYY-MM-DD");
        updateDateRangeAndFetchData(lastWeekStart, lastWeekEnd);
        break;
      }
      case "This Week": {
        updateDateRangeAndFetchData(thisWeekStart, today);
        break;
      }
      case "Last Month": {
        const lastMonthStart = dayjs()
          .subtract(1, "month")
          .startOf("month")
          .format("YYYY-MM-DD");
        const lastMonthEnd = dayjs()
          .subtract(1, "month")
          .endOf("month")
          .format("YYYY-MM-DD");
        updateDateRangeAndFetchData(lastMonthStart, lastMonthEnd);
        break;
      }
      case "This Month": {
        const thisMonthStart = dayjs().startOf("month").format("YYYY-MM-DD");
        const today = dayjs().format("YYYY-MM-DD");
        updateDateRangeAndFetchData(thisMonthStart, today);
        break;
      }
      case "Last 7 Days": {
        const sevenDaysAgo = dayjs().subtract(6, "day").format("YYYY-MM-DD");
        const today = dayjs().format("YYYY-MM-DD");
        updateDateRangeAndFetchData(sevenDaysAgo, today);
        break;
      }
      case "Last 30 Days": {
        const thirtyDaysAgo = dayjs().subtract(30, "day").format("YYYY-MM-DD");
        const today = dayjs().format("YYYY-MM-DD");
        updateDateRangeAndFetchData(thirtyDaysAgo, today);
        break;
      }
      default:
        console.log("Unexpected value:", value);
        break;
    }
  };

  const handleActionMenuItemSelect = (selectedItem) => {
    switch (selectedItem) {
      case "Add Template":
        setDrawerMode("create");
        setSelectedTemplate(null);
        updateModalVisibility("addTemplateDrawer", true);
        break;

      default:
        showToast("select correct option", "warning");
    }
  };

  const handleCloneTemplate = (rowData) => {
    setDrawerMode("clone");
    setSelectedTemplate(rowData);
    updateModalVisibility("addTemplateDrawer", true);
  };

  return (
    <div style={styles.Container}>
      {loader === true ? <Loader /> : null}

      <div style={styles.mainContent}>
        <div style={styles.headerBar}>
          <div style={styles.headerText}>
            <p>Communication Template</p>
          </div>
          <div style={styles.headerIcon} key={remountKey}>
            <MUISearchBar
              onSearch={(query) => updateFilterState("searchQuery", query)}
              placeholder="Template Name/ Template Id"
              forType="communicationTemplate"
            />
            <ActionMenu
              onSelectOption={handleActionMenuItemSelect}
              forTable="CommunicationTemplate"
            />
          </div>
        </div>

        <CommunicationTemplateFormDrawer
          open={modalVisibility.addTemplateDrawer}
          onClose={() => updateModalVisibility("addTemplateDrawer", false)}
          onSuccess={getCommunicationTemplateData}
          mode={drawerMode}
          initialData={selectedTemplate}
        />

        <Box
          sx={(theme) => ({
            display: "flex",
            backgroundColor: "#F1F4FA",
            borderRadius: "4px",
            gap: "10px",
            justifyContent: "flex-start",
            padding: "30px 0px 10px 0px",
            alignItems: "center",
            flexDirection: {
              xs: "column",
              sm: "column",
              md: "column",
              lg: "column",
              xl: "row",
            },
          })}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "20px",
            }}
            key={remountKey}
          >
            <TimelineSelector
              initialStateThisWeek={false}
              onChange={(value) => {
                setTimelineValue(value);
                handleTimelineChange(value);
              }}
              dropdownTitle="Created At"
              value={timelineValue}
            />
          </Box>

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: "10px",
              backgroundColor: "#F1F4FA",
              marginRight: "20px",
              justifyContent: "center",
            }}
          >
            {modalVisibility.datePicker && (
              <GridPairDatePicker
                resetCustomDate={resetCustomDate}
                onClick={handleCustomDateChange}
                onDateClearButtonClick={handleDateClearButton}
              />
            )}
          </Box>
        </Box>

        <div>
          <CommunicationGrid
            timeLineSelected={timeLineSelected}
            isLoading={loader}
            tableName="CommunicationTemplate"
            columns={filteredTableColumns}
            tabledata={filteredTableData}
            clearAllFilter={handleClearFilter}
            totalRecords={tableState.totalRecords}
            onPageChange={handlePageChange}
            filterChangeCounter={filterChangeCounter}
            handleRefresh={handlePageRefresh}
            onRowClick={handleLeadNameClick}
            onClone={handleCloneTemplate}
          />
        </div>
      </div>
    </div>
  );
}

export default CommunicationTemplate;

const styles = {
  Container: {
    display: "flex",
    height: "100vh",
    backgroundColor: "#F1F4FA",
  },
  mainContent: {
    flex: 1,
    overflowX: "auto",
    overflowY: "auto",
  },
  headerBar: {
    height: "30px",
    paddingTop: "30px",
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
  },
  headerText: {
    margin: 0,
    color: "#06152B",
    fontSize: "25px",
    fontWeight: "600",
    flex: "1",
  },
  headerIcon: {
    display: "flex",
    justifyContent: "space-around",
    alignItems: "center",
    gap: "35px",
    flexWrap: "wrap",
    paddingRight: "30px",
  },
};
